import React, { useEffect, useRef, useState } from 'react';
import Breadcrumb from '../components/Breadcrumbs/Breadcrumb';
import DefaultLayout from '../layout/DefaultLayout';
import userSix from '../images/user/user.png';
import { useGlobalState } from '../state';
import userService from '../services/userService';
import authService from '../services/authService';
import axios from 'axios';
import { arrayBufferToBase64 } from '../utils/formaters';
import ActionModal from '../components/ActionModal';
import { useNavigate } from 'react-router';


type UsefulDataItem = {
    afi_id?: number;
    refTipoDatoUtil?: number;
    nCaracteristica?: number | null;
    cDato?: string;
    cUsuarioCreacion?: string;
};

type DatoUtilResponseItem = {
    refTipoDatoUtil?: number;
    nCaracteristica?: number | null;
    cDato?: string;
};

type HistorialMatriculaItem = {
    tmov_mat_de?: string;
    col_nom?: string;
    afi_mov_fini?: string | null;
};

const Profile: React.FC = () => {
    const [user, setUser] = useGlobalState('user');
    const navigate = useNavigate();
    // Imagen
    const [imageUri, setImageUri] = useState<string | null>(null);
    const [initialImageUri, setInitialImageUri] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [imageChanged, setImageChanged] = useState(false);
    const [imageUpdated, setImageUpdated] = useState(false);
    const [showImageOptions, setShowImageOptions] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraKey, setCameraKey] = useState(0);

    // Estado general
    const [loading, setLoading] = useState(false);
    const [pensionado, setPensionado] = useState(false);
    const [nombreAMostrar, setNombreAMostrar] = useState('');

    // Campos generales
    const [apellido, setApellido] = useState('');
    const [nombre, setNombre] = useState('');
    const [documento, setDocumento] = useState('');
    const [cuit, setCuit] = useState('');
    const [colegio, setColegio] = useState('');
    const [matricula, setMatricula] = useState('');

    // Contactos
    const [listaTelefonos, setListaTelefonos] = useState<{ cod: string; numero: string }[]>([]);
    const [listaCelulares, setListaCelulares] = useState<{ cod: string; numero: string }[]>([]);
    const [listaEmails, setListaEmails] = useState<string[]>([]);
    const [historialMatricula, setHistorialMatricula] = useState<HistorialMatriculaItem[]>([]);

    // Form nuevo contacto
    const [codtelefono, setCodTelefono] = useState('');
    const [telefono, setTelefono] = useState('');
    const [codcelular, setCodCelular] = useState('');
    const [celular, setCelular] = useState('');
    const [email, setEmail] = useState('');

    // UI cards
    const [expandedMatricula, setExpandedMatricula] = useState(false);
    const [expandedContacto, setExpandedContacto] = useState(false);
    const [expandedNuevoContacto, setExpandedNuevoContacto] = useState(false);

    // Modal de confirmación de baja de cuenta
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    useEffect(() => {
        if (!user) return;
        const datos = (user.datosUsuario ?? user) as any;
        setApellido(datos?.apellido ?? user.apellido ?? '');
        setNombre(datos?.nombre ?? user.nombre ?? '');
        setDocumento(datos?.dni ?? '');
        setCuit(datos?.cuit ?? '');
        setColegio(datos?.nombreColegio ?? '');
        setMatricula(datos?.legajoPrevisional ?? '');

        verificarPensionado();
        cargarImagenPerfil();
        cargarDatosUtiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const verificarPensionado = () => {
        if (!user) return;
        const datos = user.datosUsuario ?? user;
        let mostrar = `${datos?.nombre ?? ''} ${datos?.apellido ?? ''}`.trim();
        if (datos?.beneficiario && String(datos.beneficiario.numeroDocumento) !== String(datos.dni)) {
            mostrar = `${datos.beneficiario.nombre} ${datos.beneficiario.apellido}`;
            setPensionado(true);
        } else {
            setPensionado(false);
        }
        setNombreAMostrar(mostrar);
    };

    const cargarImagenPerfil = async () => {
        if (!user) return;
        try {
            const leg = user.datosUsuario?.legajoPrevisional ?? user.legajoPrevisional;
            if (!leg) return;
            const res: any = await userService.getProfilePicture(String(leg));
            if (res) {
                const base64 = `data:image/jpeg;base64,${arrayBufferToBase64(res)}`;
                setImageUri(base64);
                setInitialImageUri(base64);
            }
        } catch (ex) {
            console.warn('No se pudo cargar la imagen de perfil', ex);
        }
    };

    const cargarDatosUtiles = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const leg = user.datosUsuario?.legajoPrevisional ?? user.legajoPrevisional;
            if (!leg) return;

            const datosRes: any = await userService.getDatosUtiles(String(leg));
            if (datosRes?.success) {
                const items: DatoUtilResponseItem[] = Array.isArray(datosRes.data) ? datosRes.data : [];
                setListaTelefonos(
                    items
                        .filter((i: DatoUtilResponseItem) => i.refTipoDatoUtil === 1)
                        .map((t: DatoUtilResponseItem) => ({
                            cod: String(t.nCaracteristica ?? ''),
                            numero: t.cDato ?? '',
                        }))
                );
                setListaCelulares(
                    items
                        .filter((i: DatoUtilResponseItem) => i.refTipoDatoUtil === 2)
                        .map((t: DatoUtilResponseItem) => ({
                            cod: String(t.nCaracteristica ?? ''),
                            numero: t.cDato ?? '',
                        }))
                );
                setListaEmails(
                    items
                        .filter((i: DatoUtilResponseItem) => i.refTipoDatoUtil === 3)
                        .map((t: DatoUtilResponseItem) => t.cDato ?? '')
                );
            }

            const histRes: any = await userService.getHistorialMatricula?.(String(leg));
            if (histRes?.success) {
                const historial: HistorialMatriculaItem[] = Array.isArray(histRes.data) ? histRes.data : [];
                setHistorialMatricula(historial);
                setExpandedMatricula(historial.length > 0);
            } else {
                setHistorialMatricula([]);
                setExpandedMatricula(false);
            }
        } catch (err) {
            console.error('Error cargando datos útiles', err);
        } finally {
            setLoading(false);
        }
    };

    // Selección de imagen (web)
    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] ?? null;
        setSelectedFile(f);
        if (f) {
            setImageChanged(true);
            setImageUri(URL.createObjectURL(f));
        }
    };

    const descartarCambiosImagen = () => {
        setSelectedFile(null);
        setImageUri(initialImageUri);
        setImageChanged(false);
    };

    const actualizarImagen = async () => {
        if (!selectedFile) {
            alert('Seleccione una imagen antes de guardar.');
            return;
        }
        const leg = user?.datosUsuario.datosUsuario?.legajoPrevisional ?? user?.datosUsuario.legajoPrevisional;
        if (!leg) {
            alert('ID de usuario no disponible.');
            return;
        }
        setLoading(true);
        try {
            // authService.uploadImage(afiId, file) existe en este proyecto
            const response = await authService.uploadImage(String(leg), selectedFile as File);
            if (response?.success) {
                setImageUpdated(true);
                await cargarImagenPerfil();
            } else {
                alert(response?.errors?.[0] ?? 'Error al subir la imagen.');
            }
        } catch (ex) {
            console.error('Error al actualizar imagen', ex);
            alert('Error al subir la imagen.');
        } finally {
            setLoading(false);
            setImageChanged(false);
            setSelectedFile(null);
        }
    };

    const guardarDatosContacto = async () => {
        const leg = user?.datosUsuario.datosUsuario?.legajoPrevisional ?? user?.datosUsuario.legajoPrevisional;
        if (!leg) {
            alert('ID de usuario no disponible.');
            return;
        }
        if ((codtelefono && !telefono) || (!codtelefono && telefono)) {
            alert('Debe completar ambos campos: código de área y teléfono.');
            return;
        }
        if ((codcelular && !celular) || (!codcelular && celular)) {
            alert('Debe completar ambos campos: código de área y celular.');
            return;
        }

        const datosToSend: UsefulDataItem[] = [];
        if (telefono) {
            datosToSend.push({
                afi_id: Number(leg),
                refTipoDatoUtil: 1,
                nCaracteristica: Number(codtelefono) || null,
                cDato: telefono,
                cUsuarioCreacion: 'LaCaja-Web',
            });
        }
        if (celular) {
            datosToSend.push({
                afi_id: Number(leg),
                refTipoDatoUtil: 2,
                nCaracteristica: Number(codcelular) || null,
                cDato: celular,
                cUsuarioCreacion: 'LaCaja-Web',
            });
        }
        if (email) {
            datosToSend.push({
                afi_id: Number(leg),
                refTipoDatoUtil: 3,
                nCaracteristica: null,
                cDato: email,
                cUsuarioCreacion: 'LaCaja-Web',
            });
        }

        if (datosToSend.length === 0) {
            alert('No hay datos para guardar.');
            return;
        }

        setLoading(true);
        try {
            for (const dato of datosToSend) {
                const form = new FormData();
                if (dato.afi_id) form.append('afi_id', String(dato.afi_id));
                if (dato.refTipoDatoUtil) form.append('refTipoDatoUtil', String(dato.refTipoDatoUtil));
                if (dato.nCaracteristica !== undefined && dato.nCaracteristica !== null) form.append('nCaracteristica', String(dato.nCaracteristica));
                if (dato.cDato) form.append('cDato', dato.cDato);
                form.append('cUsuarioCreacion', dato.cUsuarioCreacion ?? 'LaCaja-Web');

                // Llamada al endpoint que en el backend es /api/User/uploadusefulaffiliatedata
                await axios.post(`${process.env.REACT_APP_API_URL}/api/User/uploadusefulaffiliatedata`, form);
            }
            // refrescar lista
            await cargarDatosUtiles();
            // limpiar formulario
            setCodTelefono('');
            setTelefono('');
            setCodCelular('');
            setCelular('');
            setEmail('');
            setExpandedNuevoContacto(false);
            alert('Datos guardados correctamente.');
        } catch (ex: any) {
            console.error('Error guardando datos', ex);
            alert('Error al guardar los datos.');
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        setUser(null);
        try {
            debugger
            clearStateFromLocalstorage()
            navigate("/auth/signin")
        } catch { }
    };

    // Acción real de baja de cuenta (ejecutada cuando el usuario confirma en el modal)
    const confirmarEliminarCuenta = async () => {
        setShowDeleteModal(false);
        setLoading(true);
        try {
            // Llamada ejemplo a authService.bajarCuenta() — implementa en backend si hace falta
            const res = await userService.bajarCuenta?.();
            if (res?.success) {
                alert('Cuenta dada de baja correctamente.');
                logout();
            } else {
                alert(res?.errors?.[0] ?? 'Error al eliminar la cuenta.');
            }
        } catch (ex) {
            console.error('Error dando de baja la cuenta', ex);
            alert('Error al eliminar la cuenta.');
        } finally {
            setLoading(false);
        }
    };

    // Abre el modal de confirmación (reemplaza el uso de confirm())
    const solicitarConfirmacionEliminarCuenta = () => {
        setShowDeleteModal(true);
    };

    const cerrarCamara = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setShowCamera(false);
    };

    const abrirCamara = async () => {
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                alert('Este navegador no soporta acceso a cámara.');
                return;
            }

            setShowCamera(false);

            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }

            setCameraKey((prev) => prev + 1);

            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user' },
                audio: false,
            });

            streamRef.current = stream;
            setShowCamera(true);

            setTimeout(async () => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    try {
                        await videoRef.current.play();
                    } catch (e) {
                        console.error('No se pudo reproducir la cámara', e);
                    }
                }
            }, 150);
        } catch (error) {
            console.error(error);
            alert('No se pudo acceder a la cámara.');
            setShowCamera(false);
        }
    };

    const sacarFoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        if (video.readyState < 2 || video.videoWidth === 0) {
            alert('La cámara todavía no está lista.');
            return;
        }

        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (!blob) return;
            const file = new File([blob], 'perfil.jpg', { type: 'image/jpeg' });
            setSelectedFile(file);
            setImageUri(URL.createObjectURL(blob));
            setImageChanged(true);
        }, 'image/jpeg');

        cerrarCamara();
    };

    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    return (
        <DefaultLayout>
            <Breadcrumb pageName="Perfil" />

            <div className="max-w-4xl mx-auto p-4">
                <div className="overflow-hidden rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-6 pb-6 text-center">
                        <div className="relative mx-auto mt-20 w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-white/20 p-1">
                            <div className="relative w-full h-full rounded-full overflow-hidden shadow bg-black">
                                {showCamera ? (
                                    <video
                                        key={cameraKey}
                                        ref={videoRef}
                                        muted
                                        playsInline
                                        autoPlay
                                        className="w-full h-full object-cover"
                                    />
                                ) : imageUri ? (
                                    <img src={imageUri} alt="Perfil" className="w-full h-full object-cover" />
                                ) : (
                                    <img src={userSix} alt="Usuario" className="w-full h-full object-cover" />
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowImageOptions(true)}
                                className="absolute bottom-0 right-0 transform translate-x-1/4 translate-y-1/4 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white cursor-pointer"
                            >
                                <svg className="w-4 h-4 fill-current" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.76464 1.42638C4.87283 1.2641 5.05496 1.16663 5.25 1.16663H8.75C8.94504 1.16663 9.12717 1.2641 9.23536 1.42638L10.2289 2.91663H12.25C12.7141 2.91663 13.1592 3.101 13.4874 3.42919C13.8156 3.75738 14 4.2025 14 4.66663V11.0833C14 11.5474 13.8156 11.9925 13.4874 12.3207C13.1592 12.6489 12.7141 12.8333 12.25 12.8333H1.75C1.28587 12.8333 0.840752 12.6489 0.512563 12.3207C0.184375 11.9925 0 11.5474 0 11.0833V4.66663C0 4.2025 0.184374 3.75738 0.512563 3.42919C0.840752 3.101 1.28587 2.91663 1.75 2.91663H3.77114L4.76464 1.42638Z" />
                                    <path d="M7.00004 5.83329C6.03354 5.83329 5.25004 6.61679 5.25004 7.58329C5.25004 8.54979 6.03354 9.33329 7.00004 9.33329C7.96654 9.33329 8.75004 8.54979 8.75004 7.58329C8.75004 6.61679 7.96654 5.83329 7.00004 5.83329Z" />
                                </svg>
                            </button>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={onFileChange}
                            />
                        </div>

                        {showCamera && (
                            <div className="mt-4 flex justify-center gap-2">
                                <button
                                    type="button"
                                    onClick={cerrarCamara}
                                    className="px-4 py-2 rounded border border-black bg-white text-black text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={sacarFoto}
                                    className="px-4 py-2 rounded bg-primary text-white text-sm"
                                >
                                    Sacar foto
                                </button>
                            </div>
                        )}

                        <div className="mt-4">
                            <h3 className="text-2xl font-semibold text-black dark:text-white">
                                {user?.datosUsuario.datosUsuario?.nombre ?? user?.datosUsuario.nombre} {user?.datosUsuario.datosUsuario?.apellido ?? user?.datosUsuario.apellido}
                            </h3>
                            <p className="font-medium text-sm">{user?.datosUsuario.datosUsuario?.estado ?? user?.datosUsuario.estado}</p>
                        </div>

                        {imageChanged && (
                            <div className="flex justify-center gap-4 mt-4">
                                <button onClick={descartarCambiosImagen} className="px-4 py-2 border rounded text-sm">Descartar</button>
                                <button disabled={loading} onClick={actualizarImagen} className="px-4 py-2 bg-primario-celeste-800 text-white rounded text-sm">
                                    {loading ? 'Guardando...' : 'Guardar'}
                                </button>
                            </div>
                        )}

                        {imageUpdated && <div className="mt-2 text-sm text-green-600">Imagen de perfil actualizada</div>}
                    </div>

                    <div className="px-6 space-y-4">
                        {pensionado && (
                            <div className="bg-gray-50 rounded p-4">
                                <div className="font-semibold">Beneficiario/a</div>
                                <div className="mt-2 text-sm">{nombreAMostrar}</div>
                                <div className="text-sm">Tipo Benef.: {user?.datosUsuario.beneficiario?.tipoBeneficiario}</div>
                                <div className="text-sm">N° Doc.: {user?.datosUsuario.beneficiario?.numeroDocumento}</div>
                            </div>
                        )}

                        <div className="bg-gray-50 rounded p-4 grid gap-3 grid-cols-1 sm:grid-cols-2">
                            <div>
                                <div className="text-xs text-gray-500">Apellido</div>
                                <div className="font-medium">{apellido}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Nombre</div>
                                <div className="font-medium">{nombre}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Documento</div>
                                <div className="font-medium">{documento}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">CUIT</div>
                                <div className="font-medium">{cuit}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Colegio</div>
                                <div className="font-medium">{colegio}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Legajo Previsional</div>
                                <div className="font-medium">{matricula}</div>
                            </div>
                        </div>

                        {!pensionado && (
                            <div className="bg-gray-50 rounded p-4">
                                <div className="flex justify-between items-center">
                                    <div className="font-semibold">Movimientos de Matrícula</div>
                                    <button onClick={() => setExpandedMatricula(s => !s)} className="text-sm text-primario-celeste-800">
                                        {expandedMatricula ? 'Ocultar' : 'Mostrar'}
                                    </button>
                                </div>

                                {expandedMatricula && (
                                    <div className="mt-3 space-y-2 text-sm">
                                        <div className="grid grid-cols-3 gap-2 text-xs">
                                            <div className="font-medium">Tipo</div>
                                            <div className="font-medium">Colegio</div>
                                            <div className="font-medium">Fecha</div>
                                        </div>

                                        {historialMatricula.length > 0 ? (
                                            historialMatricula.map((m: HistorialMatriculaItem, idx: number) => (
                                                <div key={idx} className="grid grid-cols-3 gap-2 text-xs">
                                                    <div className="font-medium">{m.tmov_mat_de ?? 'N/A'}</div>
                                                    <div>{m.col_nom ?? 'N/A'}</div>
                                                    <div>{m.afi_mov_fini ? new Date(m.afi_mov_fini).toLocaleDateString() : 'N/A'}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-sm text-gray-500">No hay datos disponibles</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="bg-gray-50 rounded p-4">
                            <div className="flex justify-between items-start gap-3">
                                <div>
                                    <div className="font-semibold">Datos de Contacto</div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Los datos que se muestran corresponden a los últimos registros informados por el usuario.
                                    </p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button onClick={() => setExpandedContacto(s => !s)} className="text-sm text-primario-celeste-800">
                                        {expandedContacto ? 'Ocultar' : 'Mostrar'}
                                    </button>
                                    <button onClick={() => { setExpandedContacto(true); setExpandedNuevoContacto(true); }} className="text-sm bg-primario-celeste-800 text-white px-2 py-1 rounded">
                                        Nuevo +
                                    </button>
                                </div>
                            </div>

                            {expandedContacto && (
                                <div className="mt-3 space-y-3">
                                    {listaTelefonos.length === 0 && listaCelulares.length === 0 && listaEmails.length === 0 ? (
                                        <div className="text-sm text-gray-500">No hay datos para visualizar</div>
                                    ) : (
                                        <>
                                            {listaTelefonos.map((t, i) => (
                                                <div key={`tel-${i}`} className="flex items-center gap-3">
                                                    <div className="text-primario-celeste-800">📞</div>
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium">Teléfono</div>
                                                        <div className="text-sm">({t.cod}) {t.numero}</div>
                                                    </div>
                                                </div>
                                            ))}

                                            {listaCelulares.map((c, i) => (
                                                <div key={`cel-${i}`} className="flex items-center gap-3">
                                                    <div className="text-primario-celeste-800">📱</div>
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium">Celular</div>
                                                        <div className="text-sm">({c.cod}) {c.numero}</div>
                                                    </div>
                                                </div>
                                            ))}

                                            {listaEmails.map((e, i) => (
                                                <div key={`email-${i}`} className="flex items-center gap-3">
                                                    <div className="text-primario-celeste-800">✉️</div>
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium">Email</div>
                                                        <div className="text-sm">{e}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    {expandedNuevoContacto && (
                                        <div className="mt-3 border-t pt-3 space-y-2">
                                            <div className="rounded-md border border-primario-celeste-800/20 bg-primario-celeste-800/5 px-3 py-2">
                                                <p className="text-xs font-medium text-primario-celeste-800">
                                                    Nuevo dato de contacto
                                                </p>
                                                <p className="text-xs text-gray-600 mt-1">
                                                    Podés ingresar solo un dato (Teléfono, Celular o Email), o los que consideres necesarios.
                                                    No es obligatorio completar todos los campos.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-3 gap-2">
                                                <input className="col-span-1 border rounded px-2 py-1" placeholder="Cod. Área" value={codtelefono} onChange={(e) => setCodTelefono(e.target.value)} />
                                                <input className="col-span-2 border rounded px-2 py-1" placeholder="Teléfono" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
                                            </div>
                                            <div className="grid grid-cols-3 gap-2">
                                                <input className="col-span-1 border rounded px-2 py-1" placeholder="Cod. Área" value={codcelular} onChange={(e) => setCodCelular(e.target.value)} />
                                                <input className="col-span-2 border rounded px-2 py-1" placeholder="Celular" value={celular} onChange={(e) => setCelular(e.target.value)} />
                                            </div>
                                            <input className="w-full border rounded px-2 py-1" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                                            <div className="flex gap-3">
                                                <button onClick={() => setExpandedNuevoContacto(false)} className="px-3 py-1 border rounded">Cancelar</button>
                                                <button onClick={guardarDatosContacto} disabled={loading} className="px-3 py-1 bg-primario-celeste-800 text-white rounded">
                                                    {loading ? 'Guardando...' : 'Guardar'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-center gap-4 mt-4 pb-4">
                            <button onClick={logout} className="px-4 py-2 text-primario-celeste-800 hover:bg-primario-celeste-800 hover:text-white rounded border border-primario-celeste-800">Cerrar Sesión</button>
                            <button onClick={solicitarConfirmacionEliminarCuenta} className="px-4 py-2 text-red-600 hover:bg-red-600 hover:text-white rounded border border-red-600">Eliminar mi cuenta</button>
                        </div>
                    </div>
                </div>
            </div>

            {showImageOptions && (
                <div className="fixed inset-0 z-99999 flex items-center justify-center bg-black/50">
                    <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-lg">
                        <h3 className="text-base font-semibold text-gray-900">Cambiar foto de perfil</h3>
                        <p className="mt-2 text-sm text-gray-600">Seleccioná una opción:</p>
                        <div className="mt-4 flex flex-col gap-2">
                            <button
                                type="button"
                                className="w-full rounded-lg border border-primary text-primary py-2"
                                onClick={() => {
                                    setShowImageOptions(false);
                                    fileInputRef.current?.click();
                                }}
                            >
                                Seleccionar imagen
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-lg bg-primary text-white py-2"
                                onClick={() => {
                                    setShowImageOptions(false);
                                    abrirCamara();
                                }}
                            >
                                Tomar foto
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-lg bg-gray-100 text-gray-700 py-2"
                                onClick={() => setShowImageOptions(false)}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCamera && (
                <div className="mt-4 flex justify-center gap-2">
                    <button
                        type="button"
                        onClick={cerrarCamara}
                        className="px-4 py-2 rounded border border-black bg-white text-black text-sm"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={sacarFoto}
                        className="px-4 py-2 rounded bg-primary text-white text-sm"
                    >
                        Sacar foto
                    </button>
                </div>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />

            <ActionModal
                open={showDeleteModal}
                variant="warning"
                title="Confirmar baja de cuenta"
                message="¿Está seguro que desea eliminar su cuenta? Esta acción no se puede deshacer."
                primaryButton={{
                    label: 'SI',
                    onClick: confirmarEliminarCuenta,
                    variant: 'danger',
                    autoFocus: true,
                }}
                secondaryButton={{
                    label: 'NO',
                    onClick: () => setShowDeleteModal(false),
                    variant: 'secondary',
                }}
                onClose={() => setShowDeleteModal(false)}
            />
        </DefaultLayout>
    );
};

export default Profile;

function clearStateFromLocalstorage() {
    throw new Error('Function not implemented.');
}
